import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getLatestThreeMonthsEngagementData } from "../../helpers/utils";

// Planet component for subtle decoration
const MiniPlanet = ({
  type,
  style,
}: {
  type: string;
  style: React.CSSProperties;
}) => {
  const planetSrc = `/assets/onboarding/planet_${type}.svg`;

  if (isNaN(+type)) {
    return null;
  }

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        ...style,
        transition: "all 0.5s ease",
      }}
    >
      <img
        src={planetSrc}
        alt={`Planet ${type}`}
        className="w-full h-full object-contain"
      />
    </div>
  );
};

// Helper component for suggestion chips
const SuggestionChip = ({
  icon,
  text,
  color,
  onClick,
}: {
  icon: string;
  text: string;
  color: string;
  onClick: () => void;
}) => (
  <motion.button
    className="text-xs bg-gradient-to-r from-gray-900/80 to-black/70 text-white px-3 py-1.5 rounded-full border border-gray-800 hover:border-gray-600 transition-all flex items-center space-x-1.5 shadow-lg"
    onClick={onClick}
    whileHover={{
      scale: 1.05,
      y: -2,
      boxShadow: "0 0 8px rgba(255, 255, 255, 0.2)",
    }}
    whileTap={{ scale: 0.95 }}
    initial={{ opacity: 0.9 }}
    animate={{
      opacity: [0.9, 1, 0.9],
      y: [0, -1, 0],
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      repeatType: "reverse",
    }}
  >
    <span
      className={`${color} text-sm flex items-center justify-center`}
      style={{ textShadow: "0 0 5px currentColor" }}
    >
      {icon}
    </span>
    <span className="whitespace-nowrap">{text}</span>
  </motion.button>
);

interface ChatInputProps {
  inputValue: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  sendMessage: () => void;
  isLoading: boolean;
  isMobile?: boolean;
  autoFocus?: boolean;
  interruptRequest?: () => void;
}

// Define a handle type for the ref
export interface ChatInputHandle {
  focus: () => void;
  setValue: (value: string) => void;
}

// Use forwardRef to expose the input reference
const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      inputValue,
      handleInputChange,
      handleKeyPress,
      sendMessage,
      isLoading,
      isMobile,
      autoFocus = true,
      interruptRequest,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showPrompts, setShowPrompts] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();

        // Position cursor at the end
        if (inputRef.current?.setSelectionRange) {
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      },
      setValue: (value: string) => {
        // We need to manually trigger onChange event for React state update
        if (inputRef.current) {
          const syntheticEvent = {
            target: {
              value: value,
            },
          } as React.ChangeEvent<HTMLInputElement>;

          handleInputChange(syntheticEvent);
        }
      },
    }));

    // Detection for mobile
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

    // Setup keyboard visibility detection
    useEffect(() => {
      if (typeof window === "undefined" || !isMobile) return;

      const detectKeyboard = () => {
        if (window.visualViewport) {
          const viewportHeight = window.visualViewport.height;
          const windowHeight = window.innerHeight;
          setIsKeyboardVisible(windowHeight - viewportHeight > 150);
        }
      };

      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", detectKeyboard);
        window.visualViewport.addEventListener("scroll", detectKeyboard);
      } else {
        window.addEventListener("resize", detectKeyboard);
      }

      return () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", detectKeyboard);
          window.visualViewport.removeEventListener("scroll", detectKeyboard);
        } else {
          window.removeEventListener("resize", detectKeyboard);
        }
      };
    }, [isMobile]);

    useEffect(() => {
      if (autoFocus && inputRef.current) {
        inputRef.current.focus();
      }
    }, [autoFocus]);

    // Auto-focus input when it becomes enabled after being disabled
    useEffect(() => {
      // Focus the input when isLoading changes from true to false
      if (!isLoading && inputRef.current) {
        // Small delay to ensure the input is fully enabled
        const timer = setTimeout(() => {
          inputRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [isLoading]);

    const [cycleIndex, setCycleIndex] = useState(0);
    const [suggestions, setSuggestions] = useState([
      {
        text: "Explain a concept",
        value: "Explain Newton's laws of motion",
        emoji: "📚",
        color: "blue",
        category: "Academic Help",
      },
      {
        text: "Book a tutor session",
        value: "Connect me with a Physics tutor for the Light chapter",
        emoji: "🚀",
        color: "purple",
        category: "Blaze Sessions",
      },
      {
        text: "Find learning path",
        value: "Create a learning path for quadratic equations",
        emoji: "🧭",
        color: "amber",
        category: "Learning Paths",
      },
      {
        text: "Change settings",
        value: "Change my grade to Class 10",
        emoji: "⚙️",
        color: "emerald",
        category: "Settings",
      },
      {
        text: "Show live classes",
        value: "Show me upcoming Math live classes",
        emoji: "🔴",
        color: "red",
        category: "Live Classes",
      },
      {
        text: "Check my sessions",
        value: "Show my recent Blaze sessions",
        emoji: "📅",
        color: "indigo",
        category: "My Learning",
      },
    ]);

    // Add useEffect for auto-cycling
    const autoTimerRef = useRef<NodeJS.Timeout | null>(null);

    const resetAutoTimer = () => {
      // Clear existing timer
      if (autoTimerRef.current) {
        clearInterval(autoTimerRef.current);
      }

      // Set up a new timer
      autoTimerRef.current = setInterval(() => {
        setCycleIndex((prev) => (prev + 1) % suggestions.length);
      }, 5000); // Cycle every 5 seconds
    };

    useEffect(() => {
      // Initial setup of the timer
      resetAutoTimer();

      // Clean up on unmount
      return () => {
        if (autoTimerRef.current) {
          clearInterval(autoTimerRef.current);
        }
      };
    }, [suggestions.length]);

    // Add a function to handle user interaction with cards
    const handleCardClick = (suggestion) => {
      // First pause auto-cycling by setting the selected card as current
      setCycleIndex(suggestions.findIndex((s) => s.text === suggestion.text));

      // Reset the auto-cycling timer
      resetAutoTimer();

      // Then update the input value
      if (inputRef.current) {
        const syntheticEvent = {
          target: {
            value: suggestion.value,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        handleInputChange(syntheticEvent);
      }
    };

    // Add touch gesture handlers
    const handleSwipe = (direction) => {
      setCycleIndex((prev) => {
        if (direction === "left") {
          return (prev + 1) % suggestions.length;
        } else {
          return (prev - 1 + suggestions.length) % suggestions.length;
        }
      });

      // Reset the auto-cycling timer after swiping
      resetAutoTimer();
    };

    // Track touch interactions
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Minimum swipe distance threshold in pixels
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
      setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
      if (!touchStart || !touchEnd) return;

      const distance = touchStart - touchEnd;
      const isSwipe = Math.abs(distance) > minSwipeDistance;

      if (isSwipe) {
        if (distance > 0) {
          // Swiped left, go to next card
          handleSwipe("left");
        } else {
          // Swiped right, go to previous card
          handleSwipe("right");
        }
      }

      // Reset touch values
      setTouchStart(null);
      setTouchEnd(null);
    };

    return (
      <motion.div
        className={`p-3 md:p-4 border-t border-gray-800 bg-gradient-to-b from-[#000000] to-[#121212] backdrop-blur-sm relative overflow-visible z-20 ${
          isMobile ? "w-full" : ""
        } ${isKeyboardVisible && isMobile ? "keyboard-input-visible" : ""}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        style={{
          minHeight: isKeyboardVisible && isMobile ? "60px" : "70px",
        }}
      >
        {/* Add a subtle planet to the input area */}
        <MiniPlanet
          type="4"
          style={{
            top: "5px",
            right: "15px",
            width: "20px",
            height: "20px",
            opacity: 0.4,
            animation: "float 6s ease-in-out infinite",
          }}
        />

        {/* Animated cosmic background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Animated stars */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full"
              initial={{
                x: Math.random() * 100 + "%",
                y: Math.random() * 100 + "%",
                opacity: Math.random() * 0.5 + 0.3,
              }}
              animate={{
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 2 + Math.random() * 3,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
          ))}

          {/* Animated nebula */}
          <motion.div
            className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-tr from-gray-700/10 to-gray-500/5 blur-xl"
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, 5, 0],
              opacity: [0.3, 0.4, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* Animated comet (only shows occasionally) */}
          <AnimatePresence>
            {Math.random() > 0.7 && (
              <motion.div
                className="absolute top-0 left-0 w-1 h-1 bg-blue-200 rounded-full"
                initial={{
                  x: "0%",
                  y: "10%",
                  opacity: 0,
                }}
                animate={{
                  x: "100%",
                  y: "90%",
                  opacity: [0, 1, 0],
                  scale: [0.8, 1.5, 0.8],
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 2,
                  ease: "easeOut",
                }}
              >
                <motion.div
                  className="absolute top-0 left-0 w-6 h-1 bg-gradient-to-r from-blue-200 to-transparent rounded-full blur-sm"
                  style={{ transformOrigin: "left center", rotate: 15 }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main input container with suggestions at the top */}
        <div className="flex flex-col">
          {/* Enhanced suggestion chips - commenting out the suggestions carousel */}
          {/*
          <div
            className={`${
              !isLoading && inputValue.trim() === "" ? "pt-1 pb-5" : "h-0"
            } transition-all duration-300 overflow-hidden`}
          >
            <div className="flex flex-col space-y-1">
              <div className="flex items-center justify-between mb-1 px-1">
                <p className="text-xs text-blue-300">
                  <span className="mr-1">✨</span>
                  Try these:
                </p>

                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setCycleIndex(
                        (prev) =>
                          (prev - 1 + suggestions.length) % suggestions.length
                      );
                      resetAutoTimer();
                    }}
                    className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                    aria-label="Previous suggestion"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M15.5 18l-6-6 6-6" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setCycleIndex((prev) => (prev + 1) % suggestions.length);
                      resetAutoTimer();
                    }}
                    className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                    aria-label="Next suggestion"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8.5 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="relative h-20 w-full mb-6">
                <div
                  className="relative h-full w-full"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  {suggestions.map((suggestion, index) => {
                    // Calculate the relative index based on the cycling state
                    const relativeIndex =
                      (index - cycleIndex + suggestions.length) %
                      suggestions.length;

                    // Calculate visual properties for each card based on its relative position
                    const totalCards = suggestions.length;

                    // Reduce extreme rotation and translate values
                    const fanRotation = relativeIndex * 1.5 - 3; // More subtle rotation
                    const fanTranslateX = (relativeIndex - 1.5) * 8; // Less horizontal spread
                    const fanZIndex =
                      relativeIndex === 2
                        ? 10
                        : totalCards - Math.abs(2 - relativeIndex);
                    const isActive = relativeIndex === 2; // Center card is active

                    // Redesigned positioning - active card at center, others fanned out
                    const cardOffsetY = Math.abs(relativeIndex - 2) * 4; // More vertical offset for non-active cards

                    // Improved opacity calculation with smoother falloff
                    const opacity = Math.max(
                      0,
                      1 - Math.abs(relativeIndex - 2) * 0.25
                    );

                    // Only render cards that would be visible (optimization)
                    if (Math.abs(relativeIndex - 2) > 2) return null;

                    // Determine card color based on suggestion.color
                    const cardColor = (() => {
                      switch (suggestion.color) {
                        case "blue":
                          return "rgba(59, 130, 246, 0.5)";
                        case "purple":
                          return "rgba(139, 92, 246, 0.5)";
                        case "amber":
                          return "rgba(245, 158, 11, 0.5)";
                        case "emerald":
                          return "rgba(16, 185, 129, 0.5)";
                        case "red":
                          return "rgba(239, 68, 68, 0.5)";
                        case "indigo":
                          return "rgba(99, 102, 241, 0.5)";
                        default:
                          return "rgba(75, 85, 99, 0.5)";
                      }
                    })();

                    return (
                      <motion.div
                        key={suggestion.text}
                        className={`absolute rounded-lg shadow-lg cursor-pointer ${
                          isActive ? "ring-1 ring-blue-500/70" : ""
                        }`}
                        style={{
                          width: "94%",
                          height: "3.5rem",
                          left: "3%",
                          top: 0,
                          zIndex: fanZIndex,
                          originX: 0.5,
                          originY: 0.5, // Changed to center for better rotation
                          backgroundColor: isActive
                            ? "rgba(17, 24, 39, 0.95)"
                            : "rgba(17, 24, 39, 0.85)",
                        }}
                        initial={false} // Prevent initial animation
                        animate={{
                          opacity: opacity,
                          scale: isActive
                            ? 1.03
                            : 1 - Math.abs(relativeIndex - 2) * 0.03,
                          x: fanTranslateX,
                          y: isActive ? 0 : cardOffsetY,
                          rotateZ: fanRotation,
                          filter: `blur(${
                            Math.abs(relativeIndex - 2) * 0.7
                          }px)`,
                          boxShadow: isActive
                            ? `0 4px 12px -1px rgba(0, 0, 0, 0.3), 0 0 0 1px ${cardColor}`
                            : `0 2px 4px rgba(0, 0, 0, 0.1), 0 0 0 1px ${cardColor}33`,
                        }}
                        transition={{
                          type: "spring",
                          stiffness: 350,
                          damping: 30,
                          mass: 0.8,
                        }}
                        onClick={() => handleCardClick(suggestion)}
                        whileHover={{
                          y: isActive ? -2 : cardOffsetY - 2, // Subtle lift effect
                          scale: isActive ? 1.05 : 1,
                          filter: "blur(0px)",
                          boxShadow: `0 8px 15px -3px rgba(0, 0, 0, 0.2), 0 0 0 1px ${cardColor}`,
                        }}
                      >
                        <div
                          className={`absolute inset-0 rounded-lg bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none ${
                            isActive ? "opacity-60" : "opacity-20"
                          }`}
                        />

                        {isActive && (
                          <motion.div
                            className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
                            style={{ background: cardColor }}
                            animate={{
                              opacity: [0.6, 0.9, 0.6],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                          />
                        )}

                        <div className="p-2 h-full flex items-center relative z-10">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                              suggestion.color
                                ? `bg-${suggestion.color}-600/20 text-${suggestion.color}-500`
                                : "bg-gray-600/20 text-gray-300"
                            }`}
                          >
                            <span
                              className={`text-lg ${
                                isActive ? "text-white" : "text-gray-400"
                              }`}
                            >
                              {suggestion.emoji}
                            </span>
                          </div>

                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-sm font-medium truncate ${
                                isActive ? "text-white" : "text-gray-300"
                              }`}
                            >
                              {suggestion.category}
                            </p>
                          </div>

                          <div className="w-6 flex items-center justify-center">
                            <motion.svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 ${
                                isActive ? "text-gray-200" : "text-gray-500"
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              animate={isActive ? { x: [0, 2, 0] } : {}}
                              transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                              }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </motion.svg>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="absolute -bottom-3 left-0 right-0 flex justify-center space-x-1">
                  {suggestions.map((_, index) => (
                    <motion.button
                      key={index}
                      className="w-2 h-2 rounded-full bg-gray-600 focus:outline-none"
                      animate={{
                        scale: index === cycleIndex ? 1.2 : 1,
                        backgroundColor:
                          index === cycleIndex
                            ? "rgba(59, 130, 246, 0.8)"
                            : "rgba(75, 85, 99, 0.6)",
                      }}
                      onClick={() => {
                        setCycleIndex(index);
                        resetAutoTimer();
                      }}
                      aria-label={`Go to suggestion ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          */}

          {/* Input field container */}
          <motion.div
            className={`relative flex items-center rounded-full transition-all duration-300 ${
              isFocused
                ? "shadow-lg bg-black/40 border-gray-600 translate-y-[-2px]"
                : "shadow-md bg-black/30 border-gray-800"
            }`}
            style={{
              boxShadow: isFocused
                ? "0 10px 25px -5px rgba(75, 85, 99, 0.2), 0 8px 10px -6px rgba(75, 85, 99, 0.2), 0 0 15px rgba(75, 85, 99, 0.1)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)",
              borderWidth: "1px",
              borderStyle: "solid",
              zIndex: 20,
            }}
            animate={{
              y: [0, -2, 0],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            {/* Animated icon that transforms on focus */}
            <div className="pl-3 md:pl-4">
              <motion.div
                animate={{ rotate: isFocused ? 90 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isFocused ? (
                  <motion.svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </motion.svg>
                ) : (
                  <motion.svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    animate={{
                      rotate: [0, 5, 0, -5, 0],
                    }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </motion.svg>
                )}
              </motion.div>
            </div>

            <input
              ref={inputRef}
              type="text"
              placeholder={
                isLoading
                  ? "Processing..."
                  : "Ask a question or try a suggestion..."
              }
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onFocus={() => {
                setIsFocused(true);
              }}
              onBlur={() => {
                setIsFocused(false);
              }}
              className="flex-1 text-sm border-none outline-none bg-transparent py-3 px-3 text-white placeholder-gray-400"
              disabled={isLoading}
            />

            {/* Dynamic rocket send button with animations */}
            <div className="pr-2">
              <motion.button
                className={`w-10 h-10 rounded-full flex items-center justify-center relative overflow-hidden ${
                  isLoading
                    ? "bg-red-700/80"
                    : inputValue.trim()
                    ? "bg-gradient-to-r from-blue-700/70 to-indigo-800/70"
                    : "bg-gray-800/50"
                }`}
                whileHover={
                  isLoading || (inputValue.trim() && !isLoading)
                    ? { scale: 1.05 }
                    : {}
                }
                whileTap={
                  isLoading || (inputValue.trim() && !isLoading)
                    ? { scale: 0.95 }
                    : {}
                }
                onClick={
                  isLoading && interruptRequest ? interruptRequest : sendMessage
                }
                disabled={!isLoading && !inputValue.trim()}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                style={{
                  boxShadow:
                    isLoading || (inputValue.trim() && !isLoading)
                      ? isLoading
                        ? "0 0 15px rgba(220, 38, 38, 0.6)"
                        : inputValue.trim()
                        ? "0 0 15px rgba(59, 130, 246, 0.5)"
                        : "none"
                      : "none",
                }}
                title={isLoading ? "Stop generating" : "Send message"}
              >
                {/* Animated glow effect */}
                {(isLoading || inputValue.trim()) && !isLoading && (
                  <motion.div
                    className={`absolute inset-0 rounded-full ${
                      isLoading ? "bg-red-500" : "bg-blue-500"
                    }`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{
                      opacity: isHovering ? [0, 0.3, 0] : 0,
                      scale: isHovering ? [0.8, 1.5, 0.8] : 0.8,
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}

                {isLoading ? (
                  // Show stop icon when loading
                  <div className="relative flex items-center justify-center">
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-red-500/40"
                      initial={{ scale: 0.8, opacity: 0.6 }}
                      animate={{
                        scale: [0.8, 1.1, 0.8],
                        opacity: [0.6, 0.8, 0.6],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    <motion.svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      animate={{
                        scale: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <rect x="6" y="6" width="12" height="12" rx="2" ry="2" />
                    </motion.svg>
                  </div>
                ) : (
                  <motion.svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 relative z-10 ${
                      inputValue.trim() ? "text-white" : "text-gray-300"
                    }`}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    animate={
                      inputValue.trim()
                        ? {
                            rotate: isHovering ? [0, -10, 0] : 0,
                            y: isHovering ? [0, -2, 0] : 0,
                          }
                        : {}
                    }
                    transition={{
                      duration: 0.5,
                      ease: "easeInOut",
                    }}
                  >
                    <path d="M22.101 10.562 2.753 1.123A1.219 1.219 0 0 0 1 2.22v.035a2 2 0 0 0 .06.485l1.856 7.424a.5.5 0 0 0 .43.375l8.157.907a.559.559 0 0 1 0 1.11l-8.157.907a.5.5 0 0 0-.43.375L1.06 21.261a2 2 0 0 0-.06.485v.035a1.219 1.219 0 0 0 1.753 1.096L22.1 13.438a1.6 1.6 0 0 0 0-2.876z" />
                  </motion.svg>
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>

        {/* Add perspective styles for the suggestion cards */}
        <style jsx global>{`
          .perspective-500 {
            perspective: 500px;
          }

          .keyboard-input-visible {
            padding-top: 8px !important;
            padding-bottom: 8px !important;
            box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.5);
            transition: none !important;
            animation: none !important;
          }

          .keyboard-input-visible .blinking-star,
          .keyboard-input-visible .absolute.pointer-events-none {
            display: none !important;
          }

          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }

          @media (max-width: 767px) {
            .keyboard-input-visible + div {
              display: none !important;
            }
          }
        `}</style>
      </motion.div>
    );
  }
);

// Add display name to fix linter error
ChatInput.displayName = "ChatInput";

export default ChatInput;
